import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import {
  type Library as LibraryShape,
  type LibraryDocument,
  addVersion,
  createDocument,
  createFolder,
  deleteDocument,
  deleteFolder,
  emptyTrash,
  fetchDocument,
  fetchDocuments,
  fetchLibrary,
  fetchTrash,
  fetchVersions,
  importArchive,
  isArchive,
  limitFor,
  moveDocument,
  moveFolder,
  removeForGood,
  renameDocument,
  renameFolder,
  restore,
  uploadFile,
} from '../api/library'
import { asFailure, type Failure } from '../api/request'
import { type Loaded, useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { navigate, useRoute } from '../frame/route'
import { MARKUPS, MARKUP_FOR_A_NEW_DOCUMENT, nameOfMarkup } from '../ui/markup'
import { ComboButton } from '../ui/ComboButton'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { useAnnounce } from '../ui/Announce'
import { OpenDocument } from './library/OpenDocument'
import {
  DocumentTile,
  FolderTile,
  NamingTile,
  TrashTile,
  asSize,
  carriesFiles,
} from './library/Tiles'
import { Trash } from './library/Trash'
import { type Upload, Uploads } from './library/Uploads'
import {
  NO_FOLDER,
  TOP,
  TRASH,
  UNSORTED,
  WRITING,
  type Place,
  destinationsAmong,
  foldersIn,
  folderOf,
  holdsDocuments,
  nameOf,
  pathTo,
  placeIn,
  placeOfFolder,
  trailTo,
  VIEWS,
  VIEW_NAMES,
  viewIn,
  withView,
  isViewStep,
  type View,
} from './library/place'
import {
  DEFAULT_SORTING,
  DIRECTION_NAMES,
  ORDERS,
  ORDER_NAMES,
  naturalDescent,
  sorted,
  type Sorting,
} from './library/sorting'
import { Columns, DocumentRow, FolderRow, NamingRow, TrashRow } from './library/Rows'

/**
 * The library: everything that has been kept, in the order somebody put it in.
 *
 * Where you are comes from the address and nowhere else, so a folder can be linked to and the frame's
 * back control walks out of it. What is on the screen is a shelf of tiles — a document is its own first
 * page, not a file name beside an icon — and, when something is open, the document itself beside it.
 *
 * **Uploading is one request per file.** That is what lets each file report its own progress, lets one
 * refusal cost only that file, and makes the limit on a file the limit on a request. A `.zip` goes to
 * the other endpoint and comes back as a report, because one archive becomes many documents.
 *
 * **The limit is asked for, not assumed.** A file over it is refused here, before a byte goes out — the
 * service refuses it too, and would be the only thing standing there if this screen were wrong, but
 * ten minutes of uploading before being told is not an answer.
 */
export function Library() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  /*
   * How the shelf is arranged, off the end of the address — and the rest of the address read as if
   * it were not there. The view is in the address because the design chapter puts a view there: it
   * survives a reload and it can be sent. Taking it off first is what keeps every step below
   * meaning exactly what it meant before this existed.
   */
  const view = viewIn(route.path)
  /*
   * The steps, read straight off `route.path` by index and never out of an array built here — a
   * dependency the React compiler cannot see is settled makes it give up on the whole component.
   * A view is one of three known words and every other step is a uuid or `-`, so a step that *is*
   * one is the view and stands for nothing else.
   */
  const where = isViewStep(route.path[0]) ? undefined : route.path[0]
  const place = placeIn(where)
  const openId = isViewStep(route.path[1]) ? undefined : route.path[1]
  /** Whether the open document is being written in with nothing else beside it. */
  const writing = route.path[2] === WRITING

  const announce = useAnnounce()

  const shelf = useLoad(useCallback((signal: AbortSignal) => fetchLibrary(signal), []))
  /*
   * Which documents to fetch is one value: a folder id, or `null` for the ones that are in no folder.
   * It is read straight out of the address rather than out of the place, so that it is a string and the
   * loader beside it is stable for as long as the address is.
   *
   * `null` at the top of the library is not a special case — the documents in no folder are exactly
   * what lies there, beside the folders.
   */
  const folder =
    where === undefined || where === UNSORTED || where === NO_FOLDER || where === TRASH
      ? null
      : where
  const documents = useLoad(
    useCallback((signal: AbortSignal) => fetchDocuments(folder, signal), [folder]),
  )
  const trash = useLoad(useCallback((signal: AbortSignal) => fetchTrash(signal), []))
  /*
   * The versions of whatever is open — asked for only when there is a history to show.
   *
   * The id comes out of the address, so the loader beside it is stable for as long as the address is;
   * with nothing open there is nothing to ask, and the answer is an empty list rather than a request.
   */
  const versions = useLoad(
    useCallback(
      (signal: AbortSignal) =>
        openId === undefined ? Promise.resolve([]) : fetchVersions(openId, signal),
      [openId],
    ),
  )

  const [uploads, setUploads] = useState<readonly Upload[]>([])
  /** A folder being named. It is drawn as a tile in the shelf, so the state belongs here. */
  const [naming, setNaming] = useState(false)
  /**
   * The last thing that was refused — a name that is taken, a folder that cannot hold itself.
   *
   * These are answers rather than faults, and they used to have nowhere to appear: a rejected promise
   * in a click handler is silence, and silence is the worst possible reply to "that name is taken".
   */
  const [refused, setRefused] = useState<Failure | null>(null)
  const [dragging, setDragging] = useState(false)
  const [over, setOver] = useState(false)
  const files = useRef<HTMLInputElement>(null)
  const next = useRef(1)

  const reload = () => {
    shelf.retry()
    documents.retry()
    trash.retry()
    versions.retry()
  }

  const go = (to: Place, document?: string, alone = false) => {
    const to_ = alone ? [...pathTo(to, document), WRITING] : pathTo(to, document)
    // The arrangement travels: walking into a folder keeps the view somebody chose for the library.
    navigate('library', withView(to_, view))
  }

  /** The same place, arranged another way. An ordinary step, so the back button undoes it. */
  const arrange = (wanted: View) => {
    navigate('library', withView(route.path, wanted))
  }

  /** Does something that can be refused, and puts the refusal where it can be read. */
  const attempt = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  /**
   * Takes files in, one after another.
   *
   * Sequentially and not all at once: twenty files in parallel on a lecture-hall connection means
   * twenty uploads that all crawl, and a progress display that says nothing about which will finish
   * first. One at a time finishes the first file first.
   */
  /**
   * A document written here, in the folder that is open, opened for writing straight away.
   *
   * It is titled `Untitled` rather than asking for a name — a title is renamed where it stands a
   * moment later, and a dialog in front of writing is the thing that stops people writing. The
   * language comes from the control that made it: pressing it writes Markdown, and the chevron
   * beside it holds the others.
   */
  const write = (markup: string = MARKUP_FOR_A_NEW_DOCUMENT) => {
    void attempt(async () => {
      const made = await createDocument(folder, 'Untitled', markup)
      reload()
      go(place, made.id)
      announce('New document.')
    })
  }

  const take = async (chosen: readonly File[]) => {
    if (shelf.load.status !== 'ready') {
      return
    }
    const limits = shelf.load.value.limits
    const queued = chosen.map((file) => ({
      file,
      upload: { id: next.current++, name: file.name, state: 'waiting' as const, sent: null },
    }))
    setUploads((waiting) => [...waiting, ...queued.map((one) => one.upload)])

    for (const { file, upload } of queued) {
      const id = upload.id
      const limit = limitFor(file, limits)

      if (file.size > limit) {
        // Refused here rather than after the upload: the size is known before anything is sent, and
        // being told after ten minutes that a video was never going to be accepted is not an answer.
        update(id, {
          state: 'refused',
          reason: `${asSize(file.size)} — this MyLib takes up to ${asSize(limit)}`,
        })
        continue
      }

      update(id, { state: 'sending', sent: 0 })
      try {
        const sent = (fraction: number | null) => {
          update(id, { state: 'sending', sent: fraction })
        }
        if (isArchive(file)) {
          const report = await importArchive(file, folder, sent)
          update(id, { state: 'arrived', sent: 1, report })
        } else {
          const taken = await uploadFile(file, folder, sent)
          // "Already there" is an answer and not a failure: the file is in MyLib either way, and the
          // document it joined is the one somebody has already put in order.
          update(id, { state: taken.merged ? 'joined' : 'arrived', sent: 1 })
        }
      } catch (error) {
        update(id, { state: 'refused', reason: asFailure(error).message })
      }
      reload()
    }
    announce('Finished adding files.')
  }

  const update = (id: number, changes: Partial<Upload>) => {
    setUploads((all) =>
      all.map((upload) => (upload.id === id ? { ...upload, ...changes } : upload)),
    )
  }

  /*
   * Whether this place holds anything at all — worked out once, here, because two parts of the screen
   * answer to it: an empty view makes the offer and holds the one control that fills it, and the head
   * then does not offer the same thing a second time. At most one primary control per view.
   */
  const shown = shelf.load.status === 'ready' ? shelf.load.value : null
  const inHere =
    documents.load.status === 'ready' && holdsDocuments(place) ? documents.load.value : []
  const foldersHere = shown === null ? [] : foldersIn(place, shown.folders)
  const empty =
    shown !== null &&
    place.kind !== 'trash' &&
    // A folder being named is something on the shelf, so the offer stands aside for it.
    !naming &&
    foldersHere.length === 0 &&
    inHere.length === 0 &&
    (place.kind !== 'top' || shown.trash === 0)

  const open =
    openId === undefined || documents.load.status !== 'ready'
      ? undefined
      : documents.load.value.find((document) => document.id === openId)

  /*
   * A document that is open but does not lie here: the address is corrected to where it does lie.
   *
   * This is what arriving from a link, from a subject or from a board looks like. All three know a
   * kind and an id and deliberately nothing else, so they can only write "this document, place
   * unknown" — `#/library/-/<document>` — and without this that address would show the top of the
   * library with nothing open, or, if the place step were left out, an empty folder that never
   * existed. One request settles where the document is, and the address left standing afterwards is
   * the one that can be sent on. A bookmark to a document that has since been moved finds it again by
   * the same route.
   *
   * A document nothing can be said about — thrown away, or never there — corrects nothing: staying
   * put is more honest than moving somebody on a guess.
   */
  const ready = documents.load.status === 'ready'
  useEffect(() => {
    if (openId === undefined || open !== undefined || !ready || place.kind === 'trash') {
      return
    }
    const asking = new AbortController()
    void fetchDocument(openId, asking.signal)
      .then((document) => {
        if (document.folderId !== folder) {
          const to = pathTo(placeOfFolder(document.folderId), document.id)
          // Whatever stood over it stands over it still: an address being corrected is not a step.
          navigate('library', withView(writing ? [...to, WRITING] : to, view), { replace: true })
        }
      })
      .catch(() => {
        /* Nothing to correct it to. */
      })
    return () => {
      asking.abort()
    }
  }, [openId, open, ready, folder, place.kind, writing, view])

  return (
    <Lanes
      full={writing && open !== undefined}
      overview={{
        label: nameOf(place, shelf.load.status === 'ready' ? shelf.load.value.folders : []),
        content: (
          <div
            className={over ? 'shelf shelf--taking' : 'shelf'}
            /*
              Files from outside, and nothing else. A document being dragged from one folder to another
              also carries files — the browser fills a drag with whatever it finds in the element, which
              for a tile is its preview image — so a drop that does not check would upload the picture of
              the thing somebody was only moving.
            */
            onDragOver={(event) => {
              if (carriesFiles(event.dataTransfer)) {
                event.preventDefault()
                setOver(true)
              }
            }}
            onDragLeave={() => {
              setOver(false)
            }}
            onDrop={(event) => {
              if (carriesFiles(event.dataTransfer) && event.dataTransfer.files.length > 0) {
                event.preventDefault()
                setOver(false)
                void take([...event.dataTransfer.files])
              }
            }}
          >
            <Head
              place={place}
              shelf={shown}
              withAdd={!empty}
              onGo={go}
              onAdd={() => {
                files.current?.click()
              }}
              onNewFolder={() => {
                setRefused(null)
                setNaming(true)
              }}
              onNewDocument={write}
              onMoveFolder={(parentId) => {
                void attempt(async () => {
                  if (place.kind === 'folder') {
                    await moveFolder(place.id, parentId)
                    reload()
                    announce(parentId === null ? 'Moved to the root of the library.' : 'Moved.')
                  }
                })
              }}
              onRenameFolder={(name) => {
                void attempt(async () => {
                  if (place.kind === 'folder') {
                    await renameFolder(place.id, name)
                    reload()
                  }
                })
              }}
              onDeleteFolder={() => {
                void attempt(async () => {
                  if (place.kind !== 'folder') {
                    return
                  }
                  const up = trailTo(place, shelfFolders(shown)).at(-2)
                  await deleteFolder(place.id)
                  go(up === undefined ? { kind: 'top' } : { kind: 'folder', id: up.id })
                  reload()
                  announce('Moved to the trash.')
                })
              }}
            />

            {/*
              The one control that opens the file picker. `multiple`, because a lecture's worth of
              material arrives together; hidden, because a bare file input cannot be made to look like
              the rest of the controls and this one is opened by the button above.
            */}
            <input
              ref={files}
              className="visually-hidden"
              type="file"
              multiple
              onChange={(event) => {
                const chosen = [...(event.target.files ?? [])]
                event.target.value = ''
                void take(chosen)
              }}
            />

            <Uploads
              uploads={uploads}
              onClear={() => {
                setUploads([])
              }}
            />

            {refused !== null && <Failed failure={refused} />}

            {shelf.load.status === 'failed' && (
              <Failed
                failure={shelf.load.failure}
                onRetry={shelf.retry}
                title="The library did not load"
              />
            )}
            {shelf.load.status === 'waiting' && <Waiting label="Fetching your library." />}

            {place.kind === 'trash' && (
              <Trash
                entries={trash}
                onRestore={async (entry) => {
                  await restore(entry)
                  reload()
                  announce(`${entry.name} is back in the library.`)
                }}
                onRemove={async (entry) => {
                  await removeForGood(entry)
                  reload()
                }}
                onEmpty={async () => {
                  await emptyTrash()
                  reload()
                  announce('The trash is empty.')
                  go({ kind: 'top' })
                }}
              />
            )}

            {place.kind !== 'trash' && shown !== null && (
              <Shelf
                place={place}
                shelf={shown}
                view={view}
                onView={arrange}
                documents={documents}
                empty={empty}
                naming={naming}
                onName={(name) => {
                  void attempt(async () => {
                    const created = await createFolder(folderOf(place), name)
                    setNaming(false)
                    reload()
                    announce(`Folder ${created.name} made.`)
                  })
                }}
                onGiveUpNaming={() => {
                  setNaming(false)
                }}
                dragging={dragging}
                openId={openId}
                onGo={go}
                onAdd={() => {
                  files.current?.click()
                }}
                onWrite={write}
                onDragDocument={setDragging}
                onDropInto={async (folderId, document) => {
                  await moveDocument(document, folderId)
                  reload()
                  announce('Moved.')
                }}
              />
            )}
          </div>
        ),
      }}
      detail={
        open === undefined
          ? null
          : {
              label: open.title,
              content: (
                <OpenDocument
                  // A different document is a different thing being looked at, not the same panel with
                  // other contents in it: remounting is what makes the title field start again from
                  // what this document is called rather than from what the last one was.
                  key={open.id}
                  document={open}
                  versions={versions.load.status === 'ready' ? versions.load.value : []}
                  folders={shelf.load.status === 'ready' ? shelf.load.value.folders : []}
                  onRename={async (title) => {
                    await renameDocument(open.id, title)
                    reload()
                  }}
                  onMove={async (folderId) => {
                    await moveDocument(open.id, folderId)
                    reload()
                    // It has left this place, so what is on screen behind it is no longer where it is.
                    go(folderId === null ? TOP : { kind: 'folder', id: folderId }, open.id)
                  }}
                  onNewVersion={async (file) => {
                    const taken = await addVersion(open.id, file, () => {})
                    reload()
                    announce(
                      taken.merged
                        ? 'That file is what this document already is, so nothing was added.'
                        : `Added as version ${taken.document.version}.`,
                    )
                  }}
                  onDelete={async () => {
                    await deleteDocument(open.id)
                    announce(`${open.title} deleted.`)
                    go(place)
                    reload()
                  }}
                  onClose={() => {
                    go(place)
                  }}
                  onChanged={reload}
                  alone={writing}
                  onAlone={(wanted) => {
                    go(place, open.id, wanted)
                  }}
                />
              ),
            }
      }
    />
  )
}

/**
 * Making a document, with the languages behind a chevron.
 *
 * The control says which language it makes — *Create AsciiDoc* — rather than naming the act and
 * hiding the language inside it. A document written here is usually a long one, so AsciiDoc is the
 * press and the others are the chevron; a third language adds a line to `MARKUPS` and no control to
 * this row. See the design chapter, "One action that has variants".
 */
function WriteDocumentControl({
  tone,
  onWrite,
}: {
  readonly tone: 'primary' | 'quiet'
  readonly onWrite: (markup: string) => void
}) {
  return (
    <ComboButton
      label={`Create ${nameOfMarkup(MARKUP_FOR_A_NEW_DOCUMENT)}`}
      tone={tone}
      onAct={() => {
        onWrite(MARKUP_FOR_A_NEW_DOCUMENT)
      }}
      moreLabel="Create a document in another markup"
      actions={MARKUPS.filter((markup) => markup !== MARKUP_FOR_A_NEW_DOCUMENT).map((markup) => ({
        id: markup,
        label: `Create ${nameOfMarkup(markup)}`,
      }))}
      onChoose={onWrite}
    />
  )
}

function shelfFolders(shelf: LibraryShape | null) {
  return shelf?.folders ?? []
}

interface HeadProps {
  readonly place: Place
  readonly shelf: LibraryShape | null
  /** Left out where the view is empty: the offer there holds that control instead. */
  readonly withAdd: boolean
  readonly onGo: (to: Place) => void
  readonly onAdd: () => void
  /** Starts naming a new folder. The naming itself happens on a tile in the shelf. */
  readonly onNewFolder: () => void
  /** Makes a document in that markup to write here, and opens it. */
  readonly onNewDocument: (markup: string) => void
  readonly onRenameFolder: (name: string) => void
  /** Puts the folder somewhere else. `null` is the top of the library. */
  readonly onMoveFolder: (parentId: string | null) => void
  readonly onDeleteFolder: () => void
}

/**
 * Where you are, and what can be done here.
 *
 * The trail is the way back out and doubles as the answer to "where am I": on a phone it is the only
 * one, because the folder tree is not standing beside the content there.
 *
 * A folder's own controls — renaming it, deleting it — live here, inside the folder, rather than on
 * every tile. That is deliberate: a menu per tile would be a right-click on a desktop and a long press
 * on a phone, and MyLib has neither.
 */
function Head({
  place,
  shelf,
  withAdd,
  onGo,
  onAdd,
  onNewFolder,
  onNewDocument,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
}: HeadProps) {
  const [renaming, setRenaming] = useState(false)
  const trail = trailTo(place, shelfFolders(shelf))
  const here = trail.at(-1)

  return (
    <header className="shelf__head">
      <nav className="trail" aria-label="Where you are">
        <button
          type="button"
          className="trail__step"
          onClick={() => {
            onGo({ kind: 'top' })
          }}
        >
          Library
        </button>
        {trail.map((step, index) => (
          <Fragment key={step.id}>
            <span className="trail__slash" aria-hidden="true">
              /
            </span>
            {index === trail.length - 1 ? (
              /*
              The folder you are in is renamed where its name stands, exactly as a document's title is.
              A field somewhere else with a Done button beside it would be a form for changing one word.
            */
              renaming ? (
                <TrailField
                  initial={step.name}
                  onDone={(name) => {
                    setRenaming(false)
                    if (name !== step.name) {
                      onRenameFolder(name)
                    }
                  }}
                  onGiveUp={() => {
                    setRenaming(false)
                  }}
                />
              ) : (
                <span className="trail__here">{step.name}</span>
              )
            ) : (
              <button
                type="button"
                className="trail__step"
                onClick={() => {
                  onGo({ kind: 'folder', id: step.id })
                }}
              >
                {step.name}
              </button>
            )}
          </Fragment>
        ))}
      </nav>

      <div className="shelf__doing">
        {withAdd && (
          <Button tone="primary" onClick={onAdd}>
            Add files
          </Button>
        )}
        <WriteDocumentControl tone="quiet" onWrite={onNewDocument} />
        <Button tone="quiet" onClick={onNewFolder}>
          New folder
        </Button>
        {here !== undefined && (
          <>
            <Button
              tone="bare"
              onClick={() => {
                setRenaming(true)
              }}
            >
              Rename
            </Button>
            {/*
              No question asked. It goes to the trash with everything in it, as one entry, and comes
              back from there in one control — so there is nothing here worth interrupting somebody for.
            */}
            <Button tone="bare" className="control--danger" onClick={onDeleteFolder}>
              Move folder to trash
            </Button>
          </>
        )}
      </div>

      {/*
        Where this folder sits. Its own row and stacked, not beside the buttons: a field carries its
        label above its control, so a field in a row of buttons is two lines beside one and nothing
        shares a line.
      */}
      {here !== undefined && (
        <div className="shelf__setting">
          <label className="field">
            <span className="field__label">This folder sits in</span>
            <select
              className="field__input"
              value={here.parentId ?? ''}
              onChange={(event) => {
                onMoveFolder(event.target.value === '' ? null : event.target.value)
              }}
            >
              <option value="">Root</option>
              {destinationsAmong(shelfFolders(shelf), here.id).map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </header>
  )
}

/**
 * The name of the folder you are in, edited where it stands.
 *
 * One field and nothing else: Enter or leaving it keeps the new name, Escape puts back the old one. It
 * sits in the trail, in the trail's own type, so there is nothing to align it against.
 */
function TrailField({
  initial,
  onDone,
  onGiveUp,
}: {
  readonly initial: string
  readonly onDone: (name: string) => void
  readonly onGiveUp: () => void
}) {
  const [name, setName] = useState(initial)
  const [settled, setSettled] = useState(false)

  const keep = () => {
    if (settled) {
      return
    }
    setSettled(true)
    const wanted = name.trim()
    if (wanted === '') {
      onGiveUp()
    } else {
      onDone(wanted)
    }
  }

  return (
    <input
      className="trail__field"
      // Focused on appearing: a control was just used to ask for exactly this field.
      autoFocus
      aria-label="Name of this folder"
      value={name}
      onChange={(event) => {
        setName(event.target.value)
      }}
      onBlur={keep}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          keep()
        }
        if (event.key === 'Escape') {
          setSettled(true)
          onGiveUp()
        }
      }}
    />
  )
}

interface ShelfProps {
  readonly place: Place
  readonly shelf: LibraryShape
  /** How the shelf is arranged, and the way to arrange it otherwise. Both come from the address. */
  readonly view: View
  readonly onView: (view: View) => void
  readonly documents: Loaded<LibraryDocument[]>
  /** Whether this place holds nothing at all, decided by the area rather than here. */
  readonly empty: boolean
  /** Whether a new folder is being named. It appears as a tile, before the folders that exist. */
  readonly naming: boolean
  readonly onName: (name: string) => void
  readonly onGiveUpNaming: () => void
  readonly dragging: boolean
  readonly openId: string | undefined
  readonly onGo: (to: Place, document?: string) => void
  readonly onAdd: () => void
  /** Makes a document in that markup to write here, and opens it. */
  readonly onWrite: (markup: string) => void
  /** Says whether a document is being dragged right now, so folders can offer themselves. */
  readonly onDragDocument: (dragging: boolean) => void
  readonly onDropInto: (folderId: string, document: string) => Promise<void>
}

/**
 * What is here: the folders, then what lies in them.
 *
 * **Three arrangements and four orders, and both are choices rather than settings.** The arrangement
 * is in the address, because it is a way of looking that somebody may want to send or come back to;
 * the order is held here, because it is a way of looking at what is already on the screen. Sorting is
 * done on this side — a folder holds tens of documents, not thousands, and a sort that needs a
 * request is a sort that flickers.
 *
 * **Folders never take part in the order.** They have no size and no kind, and a folder that wandered
 * in among the files whenever the criterion changed would be movement without meaning. They stand in
 * front, by name, in every view and under every order.
 */
function Shelf({
  place,
  shelf,
  view,
  onView,
  documents,
  empty,
  naming,
  onName,
  onGiveUpNaming,
  dragging,
  openId,
  onGo,
  onAdd,
  onWrite,
  onDragDocument,
  onDropInto,
}: ShelfProps) {
  const [how, setHow] = useState<Sorting>(DEFAULT_SORTING)
  /*
   * Which subject the shelf is narrowed to, or none.
   *
   * Held here rather than in the address: it is a way of looking at what is already on the screen,
   * not a place. It filters in the browser over the list that was fetched anyway, because a folder
   * holds tens of documents and not thousands — a filter that needed a request would flicker.
   */
  const [narrowedTo, setNarrowedTo] = useState<string | null>(null)
  const here = foldersIn(place, shelf.folders)
  const all =
    documents.load.status === 'ready' && holdsDocuments(place)
      ? sorted(documents.load.value, how)
      : []
  const inside = narrowedTo === null ? all : all.filter((one) => isAbout(one, narrowedTo))
  const subjects = subjectsAmong(all)
  if (empty) {
    return (
      <Offer
        title={place.kind === 'top' ? 'Your library' : 'Nothing in here yet'}
        lead={
          place.kind === 'top'
            ? 'Slides, scripts, handouts, photographs of a whiteboard — everything you keep for a course lives here, and shows itself as what it is rather than as a row of file names. Anything you have not put into a folder yet simply lies here.'
            : 'Put something in it: drag files onto this page, or use “Add files”. A ZIP archive is unpacked and keeps the folders it brought.'
        }
        action={
          <>
            <Button tone="primary" onClick={onAdd}>
              Add files
            </Button>
            {/*
              Beside adding, not instead of it: material arrives both ways, and an empty library that
              only offered an upload would say that writing here is an afterthought.
            */}
            <WriteDocumentControl tone="quiet" onWrite={onWrite} />
          </>
        }
      />
    )
  }

  return (
    <>
      {subjects.length > 0 && (
        /*
          What is on this shelf, by subject.
          
          A chip row over the shelf and not a second screen: narrowing to a subject is a glance, and
          a subject a document has through its folder narrows it just as one somebody filed by hand
          does — which is the whole promise of the inheritance.
        */
        <div className="filter-subjects" role="group" aria-label="Narrow to a subject">
          <button
            type="button"
            className="filter-subjects__chip"
            aria-pressed={narrowedTo === null}
            onClick={() => {
              setNarrowedTo(null)
            }}
          >
            Everything
          </button>
          {subjects.map((subject) => (
            <button
              type="button"
              key={subject.id}
              className="filter-subjects__chip"
              aria-pressed={narrowedTo === subject.id}
              onClick={() => {
                setNarrowedTo((before) => (before === subject.id ? null : subject.id))
              }}
            >
              {subject.name}
            </button>
          ))}
        </div>
      )}

      {/*
        How it is arranged. Named in words and not in three little pictures — the design chapter is
        explicit about it, and a grid-versus-list icon pair is exactly the toggle nobody can read.
      */}
      <div className="shelf__ways" role="group" aria-label="How to show these">
        {VIEWS.map((way) => (
          <Button
            key={way}
            tone={view === way ? 'primary' : 'bare'}
            aria-pressed={view === way}
            onClick={() => {
              onView(way)
            }}
          >
            {VIEW_NAMES[way]}
          </Button>
        ))}
      </div>

      {inside.length > 1 && (
        <div className="shelf__sort">
          <div role="group" aria-label="Sort by">
            {ORDERS.map((order) => (
              <Button
                key={order}
                tone={how.by === order ? 'quiet' : 'bare'}
                aria-pressed={how.by === order}
                onClick={() => {
                  /*
                    Choosing the criterion that is already in force turns it round, and choosing a
                    different one starts it the way that criterion is usually wanted — newest first
                    for a date, largest first for a size. One control, both questions, and no arrow
                    that means "ascending" over a column of file sizes.
                  */
                  setHow((before) =>
                    before.by === order
                      ? { by: order, descending: !before.descending }
                      : { by: order, descending: naturalDescent(order) },
                  )
                }}
              >
                {ORDER_NAMES[order]}
              </Button>
            ))}
          </div>
          {/*
            The direction, said in the words of the criterion it applies to: "Newest first" rather
            than "descending", which is true of all four and tells nobody anything.
          */}
          <Button
            tone="bare"
            icon={how.descending ? 'down' : 'up'}
            onClick={() => {
              setHow((before) => ({ ...before, descending: !before.descending }))
            }}
          >
            {how.descending ? DIRECTION_NAMES[how.by].down : DIRECTION_NAMES[how.by].up}
          </Button>
        </div>
      )}

      {/*
        The same things in whichever shape was asked for. One list and one set of children rather
        than three screens: what a thing *is* and what can be done to it does not change with how
        densely it is drawn, and a second copy of the shelf would be a second place for a bug.
      */}
      {view === 'tiles' ? (
        <ul className="tiles">
          {naming && <NamingTile initial="" onDone={onName} onGiveUp={onGiveUpNaming} />}
          {place.kind === 'top' && shelf.trash > 0 && (
            <TrashTile
              count={shelf.trash}
              onOpen={() => {
                onGo({ kind: 'trash' })
              }}
            />
          )}
          {here.map((folder) => (
            <FolderTile
              key={folder.id}
              folder={folder}
              offering={dragging}
              onOpen={() => {
                onGo({ kind: 'folder', id: folder.id })
              }}
              onDrop={(moved) => {
                void onDropInto(folder.id, moved)
              }}
            />
          ))}
          {inside.map((document) => (
            <DocumentTile
              key={document.id}
              document={document}
              open={document.id === openId}
              onOpen={() => {
                onGo(place, document.id)
              }}
              onDragStart={() => {
                onDragDocument(true)
              }}
              onDragEnd={() => {
                onDragDocument(false)
              }}
            />
          ))}
        </ul>
      ) : (
        <ul className={view === 'details' ? 'rows rows--details' : 'rows'}>
          {view === 'details' && <Columns />}
          {naming && <NamingRow initial="" view={view} onDone={onName} onGiveUp={onGiveUpNaming} />}
          {place.kind === 'top' && shelf.trash > 0 && (
            <TrashRow
              count={shelf.trash}
              view={view}
              onOpen={() => {
                onGo({ kind: 'trash' })
              }}
            />
          )}
          {here.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              view={view}
              offering={dragging}
              onOpen={() => {
                onGo({ kind: 'folder', id: folder.id })
              }}
              onDrop={(moved) => {
                void onDropInto(folder.id, moved)
              }}
            />
          ))}
          {inside.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              view={view}
              open={document.id === openId}
              onOpen={() => {
                onGo(place, document.id)
              }}
              onDragStart={() => {
                onDragDocument(true)
              }}
              onDragEnd={() => {
                onDragDocument(false)
              }}
            />
          ))}
        </ul>
      )}

      {documents.load.status === 'waiting' && <Waiting label="Fetching what is in here." />}
      {documents.load.status === 'failed' && (
        <Failed failure={documents.load.failure} onRetry={documents.retry} />
      )}
    </>
  )
}

/**
 * The subjects that appear anywhere on this shelf, by name.
 *
 * Read off the documents rather than fetched: they carry their subjects already, so the row is a
 * consequence of what is on the screen and can never offer a subject that would narrow to nothing.
 */
function subjectsAmong(documents: readonly LibraryDocument[]): { id: string; name: string }[] {
  const found = new Map<string, string>()
  documents.forEach((one) => {
    ;(one.subjects ?? []).forEach((subject) => {
      found.set(subject.id, subject.name)
    })
  })
  return [...found.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((one, other) => one.name.localeCompare(other.name))
}

/** Whether a document belongs to this subject — directly or through the folder it lies in. */
function isAbout(document: LibraryDocument, subjectId: string): boolean {
  return (document.subjects ?? []).some((subject) => subject.id === subjectId)
}
